import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    TabsTriggerDynamicStyleType
} from "./type";
import type {
    Mutable
} from "../../../../types";
import {
    getFocusRingStyle
} from "../../../../helpers/theme/state";
import {
    getCornerStyle
} from "../../../../helpers/theme/corner";
import {
    getTabsThumbStyle
} from "../../stylesheet";
import {
    webStyle
} from "../../../../utils";

const stylesheet = StyleSheet.create({
    start: {
        justifyContent: "flex-start"
    },
    container: {
        justifyContent: "center",
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    surface: {
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "row"
    },
    content: {
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "row",
        flexShrink: 1,
        minWidth: 0
    },
    hidden: {
        pointerEvents: "none",
        position: "absolute",
        opacity: 0,
        ...webStyle({
            visibility: "hidden"
        })
    },
    layer: {
        pointerEvents: "none",
        position: "absolute"
    },
    stretch: {
        alignSelf: "stretch"
    },
    fill: {
        flexShrink: 1,
        flexBasis: 0,
        flexGrow: 1,
        minWidth: 0
    },
    shrink: {
        flexShrink: 1,
        minWidth: 0
    },
    label: {
        flexShrink: 1
    },
    icon: {
        flexShrink: 0
    },
    fixed: {
        flexShrink: 0
    },
    grow: {
        flexGrow: 1
    }
});

export const useStyles = ({
    currentMeasures,
    spreadBehaviour,
    isFocusVisible,
    isMarkVisible,
    shapeConfigs,
    orientation,
    isHidden,
    radiuses,
    borders,
    isFixed,
    variant,
    colors,
    spaces
}: TabsTriggerDynamicStyleType) => {
    const isVertical = orientation === "vertical";

    const isSegment = variant === "segment";

    const styles = {
        surface: {
            paddingStart: currentMeasures.paddingStart,
            paddingEnd: currentMeasures.paddingEnd,
            minHeight: currentMeasures.itemHeight
        } as Mutable<ViewStyle>,
        container: {
            minHeight: currentMeasures.cellHeight
        } as Mutable<ViewStyle>,
        layer: {
            ...getCornerStyle({
                radius: radiuses.actions,
                shapeConfigs
            })
        } as Mutable<ViewStyle>,
        label: {} as Mutable<ViewStyle>,
        content: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>
    };

    if(currentMeasures.cellInset) {
        Object.assign(styles.container, {
            paddingHorizontal: currentMeasures.cellInset,
            marginHorizontal: -currentMeasures.cellInset
        });
    }

    if(isFocusVisible) {
        Object.assign(styles.container, {
            zIndex: shapeConfigs.layers.raised
        });
    }

    if(isVertical) {
        Object.assign(styles.container, stylesheet.stretch);

        Object.assign(styles.surface, stylesheet.start);

        Object.assign(styles.content, stylesheet.start, stylesheet.grow);

        Object.assign(styles.label, stylesheet.grow);
    } else if(spreadBehaviour === "stretch") {
        Object.assign(styles.container, stylesheet.fill);
    } else {
        Object.assign(styles.container, isFixed ? stylesheet.fixed : stylesheet.shrink);
    }

    if(isHidden) {
        Object.assign(styles.container, stylesheet.hidden);
    }

    if(isSegment) {
        Object.assign(styles.surface, getCornerStyle({
            radius: currentMeasures.itemRadius,
            shapeConfigs
        }));

        if(isMarkVisible) {
            Object.assign(styles.surface, getTabsThumbStyle({
                measures: currentMeasures,
                shapeConfigs,
                colors
            }));
        }

        Object.assign(styles.surface, getFocusRingStyle({
            isFocusVisible,
            borders,
            colors
        }));
    } else {
        const markColor = isMarkVisible ? colors.content.text.high : "transparent";

        if(isVertical) {
            Object.assign(styles.surface, {
                borderStartWidth: borders.focus,
                borderStartColor: markColor
            });

            Object.assign(styles.layer, {
                bottom: spaces.spacingXxs,
                start: spaces.spacingXs,
                top: spaces.spacingXxs,
                end: spaces.spacingXs
            });
        } else {
            Object.assign(styles.surface, {
                borderBottomWidth: borders.focus,
                borderBottomColor: markColor
            });

            Object.assign(styles.layer, {
                start: -spaces.spacingSm,
                bottom: spaces.spacingXs,
                end: -spaces.spacingSm,
                top: spaces.spacingXs
            });
        }

        Object.assign(styles.layer, getFocusRingStyle({
            isFocusVisible,
            borders,
            colors
        }));
    }

    return styles;
};
export default stylesheet;
